import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BASE as API_BASE } from "../services/api";
import { useToast } from "../context/ToastContext";
import {
  RH_FIELDS,
  RH_REQUIRED_FIELDS,
  camposFaltantes,
  PERMISSOES,
  PERMISSOES_PLURAL,
  labelPermissao,
} from "./rhFields";
import { useEquipes } from "../context/EquipesContext";
import { baixarArquivo, gerarCsv, gerarXlsx, TIPO_XLSX } from "../services/planilha";
import "../assets/css/RHFila.css";

/* RH · Pessoas — diretório no padrão de BambooHR / Google Workspace Admin:
 * abas por situação (Ativos primeiro), filtros e alertas, tabela com seleção e menu por
 * linha, e uma ficha com TODAS as seções numa rolagem só, todos os campos editáveis.
 *
 * Situação vem dos dois campos que o back grava:
 *   ativo           = ativo true e não desligado
 *   pendente        = ativo false e não desligado (cadastro novo)
 *   desligado       = desligado true (o back força ativo=false e status=Desligado)
 * Contas antigas sem `id_usuarios` são editadas por `pk:<id>` (usuarios_service.filtro_usuario). */

async function apiFetch(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}

const refDe = (u) => (u?.id_usuarios ? String(u.id_usuarios) : `pk:${u?.id}`);
const nomeDe = (u) => u?.nome || u?.username || "Sem nome";
const sim = (v) => v === true || String(v) === "true";

function inputValue(value) {
  if (value === true) return "true";
  if (value === false) return "false";
  return value ?? "";
}

function situacaoDe(u) {
  if (sim(u?.desligado)) return "desligado";
  return u?.ativo ? "ativo" : "pendente";
}

const SITUACAO = {
  ativo: { rotulo: "Ativo", classe: "is-ativo" },
  pendente: { rotulo: "Pendente", classe: "is-pendente" },
  desligado: { rotulo: "Desligado", classe: "is-desligado" },
};

/** Combinações que não deveriam existir — mostradas, com correção em um clique. */
function inconsistencias(u) {
  const sit = situacaoDe(u);
  const lista = [];
  if (sit !== "desligado" && u?.data_desligamento) {
    lista.push({ id: "data_sem_desligado", texto: `Tem data de desligamento (${dataBR(u.data_desligamento)}) mas está ${sit === "ativo" ? "ativo" : "pendente"}.` });
  }
  if (sit === "ativo" && String(u?.status || "") === "Desligado") lista.push({ id: "status_desligado", texto: "Status RH diz Desligado, mas o acesso está ativo." });
  if (!u?.id_usuarios) lista.push({ id: "sem_id", texto: "Conta antiga sem ID de usuário (C61…): editável, mas não aparece nos rankings e seletores." });
  return lista;
}

const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const DIAS_SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function dateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
function addDays(date, days) { const n = new Date(date); n.setDate(n.getDate() + days); return n; }
function calcularPascoa(ano) {
  const a = ano % 19, b = Math.floor(ano / 100), c = ano % 100, d = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  return new Date(ano, Math.floor((h + l - 7 * m + 114) / 31) - 1, ((h + l - 7 * m + 114) % 31) + 1);
}
function feriadosBrasil(ano) {
  const p = calcularPascoa(ano);
  const fixos = [[1, 1, "Confraternização Universal"], [4, 21, "Tiradentes"], [5, 1, "Dia do Trabalho"], [9, 7, "Independência do Brasil"],
    [10, 12, "Nossa Senhora Aparecida"], [11, 2, "Finados"], [11, 15, "Proclamação da República"], [11, 20, "Consciência Negra"], [12, 25, "Natal"]]
    .map(([mm, dd, titulo]) => ({ key: dateKey(new Date(ano, mm - 1, dd)), titulo, tipo: "feriado" }));
  const moveis = [[addDays(p, -48), "Carnaval"], [addDays(p, -47), "Carnaval"], [addDays(p, -2), "Sexta-feira Santa"], [p, "Páscoa"], [addDays(p, 60), "Corpus Christi"]]
    .map(([dt, titulo]) => ({ key: dateKey(dt), titulo, tipo: "feriado" }));
  return [...fixos, ...moveis];
}

const hojeZero = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
function dataBR(iso) { return iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString("pt-BR") : "—"; }
function diasCreci(u) {
  if (!u?.validade_creci) return null;
  return Math.ceil((new Date(`${u.validade_creci}T00:00:00`) - hojeZero()) / 86400000);
}
function percentualCadastro(u) {
  const total = RH_REQUIRED_FIELDS.length;
  return Math.round(((total - Math.min(camposFaltantes(u).length, total)) / total) * 100);
}
function iniciais(u) {
  const partes = nomeDe(u).trim().split(/\s+/);
  return ((partes[0]?.[0] || "") + (partes.length > 1 ? partes[partes.length - 1][0] : "")).toUpperCase();
}
const CORES_AVATAR = ["#e1005b", "#6d5ce7", "#1c9b6c", "#2f6c96", "#e49b38", "#b03040", "#4a52c9"];
function corAvatar(u) {
  const s = refDe(u);
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 997;
  return CORES_AVATAR[h % CORES_AVATAR.length];
}

/* Seções da ficha — todas visíveis numa rolagem, com índice lateral. Nenhum campo de fora. */
const SECOES = [
  { id: "acesso", rotulo: "Acesso ao sistema", campos: ["username", "permissao", "team", "id_imoview", "email", "telefone"] },
  // CRECI é dado da pessoa (o registro é dela, não da 61) e ela mesma mantém em "Meus dados".
  { id: "pessoais", rotulo: "Dados pessoais", campos: ["nome", "data_nascimento", "estado_civil", "possui_filhos", "cpf", "rg", "creci", "validade_creci", "endereco"] },
  { id: "contatos", rotulo: "Contatos", campos: ["telefone_corporativo", "email_corporativo", "telefone_pessoal", "email_pessoal", "contato_emergencia"] },
  { id: "rh", rotulo: "Vínculo com a 61", campos: ["status", "unidade", "gerente_responsavel", "data_entrada_61"] },
  { id: "documentos", rotulo: "Documentos e onboarding", campos: ["contrato_assinado", "codigo_conduta_assinado", "lgpd_assinada", "onboarding_realizado", "link_documentos"] },
  { id: "financeiro", rotulo: "Dados financeiros", campos: ["cnpj", "razao_social", "banco", "agencia", "conta", "tipo_conta", "chave_pix"] },
  { id: "desligamento", rotulo: "Desligamento e observações", campos: ["desligado", "data_desligamento", "observacoes"] },
];
const CAMPO_BASE = {
  username: { name: "username", label: "Username (login)" },
  permissao: { name: "permissao", label: "Permissão", type: "permissao" },
  team: { name: "team", label: "Equipe", type: "equipe" },
  id_imoview: { name: "id_imoview", label: "Código Imoview", placeholder: "Ex.: 112" },
  email: { name: "email", label: "E-mail de login", type: "email" },
  telefone: { name: "telefone", label: "Telefone de login" },
};
const CAMPO = { ...Object.fromEntries(RH_FIELDS.map((f) => [f.name, f])), ...CAMPO_BASE };
const CAMPOS_FORM = SECOES.flatMap((s) => s.campos);
const SECAO_DO_CAMPO = Object.fromEntries(SECOES.flatMap((s) => s.campos.map((c) => [c, s.id])));
const formDe = (u) => Object.fromEntries(CAMPOS_FORM.map((c) => [c, inputValue(u?.[c])]));

/* Filtros avançados: qualquer campo do cadastro + condição + valor, todas combinadas (E). */
const CAMPOS_FILTRO = [{ name: "id_usuarios", label: "ID do usuário" }, ...CAMPOS_FORM.map((c) => CAMPO[c])];
const CAMPO_FILTRO = Object.fromEntries(CAMPOS_FILTRO.map((f) => [f.name, f]));
function tipoFiltro(nome) {
  const f = CAMPO_FILTRO[nome] || {};
  if (f.type === "boolean") return "boolean";
  if (f.type === "date") return "date";
  if (f.type === "select" || f.type === "permissao" || f.type === "equipe") return "select";
  return "text";
}
const OPERADORES = {
  text: [["contem", "contém"], ["nao_contem", "não contém"], ["igual", "é igual a"], ["preenchido", "está preenchido"], ["vazio", "está vazio"]],
  select: [["igual", "é"], ["diferente", "não é"], ["preenchido", "está preenchido"], ["vazio", "está vazio"]],
  date: [["desde", "a partir de"], ["ate", "até"], ["preenchido", "está preenchida"], ["vazio", "está vazia"]],
  boolean: [["sim", "é Sim"], ["nao", "é Não"], ["vazio", "não informado"]],
};
const SEM_VALOR = new Set(["preenchido", "vazio", "sim", "nao"]);
const ATALHOS = [
  { rotulo: "Sem código Imoview", campo: "id_imoview", op: "vazio" },
  { rotulo: "Sem CPF", campo: "cpf", op: "vazio" },
  { rotulo: "Sem e-mail corporativo", campo: "email_corporativo", op: "vazio" },
  { rotulo: "Sem telefone corporativo", campo: "telefone_corporativo", op: "vazio" },
  { rotulo: "Sem gerente responsável", campo: "gerente_responsavel", op: "vazio" },
  { rotulo: "Contrato não assinado", campo: "contrato_assinado", op: "nao" },
  { rotulo: "LGPD não assinada", campo: "lgpd_assinada", op: "nao" },
  { rotulo: "Sem CRECI", campo: "creci", op: "vazio" },
  { rotulo: "Sem link de documentos", campo: "link_documentos", op: "vazio" },
];
const normal = (v) => String(v ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
function atende(u, { campo, op, valor }) {
  const bruto = u?.[campo];
  const vazio = bruto === null || bruto === undefined || String(bruto).trim() === "";
  if (op === "preenchido") return !vazio;
  if (op === "vazio") return vazio;
  if (op === "sim") return sim(bruto);
  if (op === "nao") return !vazio && !sim(bruto);
  if (!String(valor ?? "").trim()) return true;                 // condição ainda sem valor: não filtra
  if (op === "desde") return !vazio && String(bruto).slice(0, 10) >= valor;
  if (op === "ate") return !vazio && String(bruto).slice(0, 10) <= valor;
  if (op === "igual") return normal(bruto) === normal(valor);
  if (op === "diferente") return normal(bruto) !== normal(valor);
  if (op === "contem") return normal(bruto).includes(normal(valor));
  if (op === "nao_contem") return !normal(bruto).includes(normal(valor));
  return true;
}

const ABAS = [
  { id: "ativo", rotulo: "Ativos" },
  { id: "pendente", rotulo: "Pendentes de ativação" },
  { id: "desligado", rotulo: "Desligados" },
  { id: "todos", rotulo: "Todos" },
];
const ALERTAS = [
  { id: "documentacao", rotulo: "Cadastro incompleto", teste: (u) => camposFaltantes(u).length > 0 },
  { id: "creci", rotulo: "CRECI vencido ou vencendo", teste: (u) => { const d = diasCreci(u); return d != null && d <= 30; } },
  { id: "aniversario", rotulo: "Aniversário no mês", teste: (u) => Boolean(u.data_nascimento) && Number(String(u.data_nascimento).slice(5, 7)) === new Date().getMonth() + 1 },
  { id: "inconsistente", rotulo: "Precisa de revisão", teste: (u) => inconsistencias(u).some((i) => i.id !== "sem_id") },
];

const COLUNAS = [
  { id: "nome", rotulo: "Pessoa", valor: (u) => nomeDe(u).toLowerCase() },
  { id: "equipe", rotulo: "Equipe" },
  { id: "permissao", rotulo: "Permissão", valor: (u) => labelPermissao(u.permissao) },
  { id: "telefone", rotulo: "Telefone", valor: (u) => u.telefone_corporativo || u.telefone || "" },
  { id: "cadastro", rotulo: "Cadastro", valor: (u) => percentualCadastro(u) },
  { id: "creci", rotulo: "CRECI", valor: (u) => { const d = diasCreci(u); return d == null ? 99999 : d; } },
  { id: "situacao", rotulo: "Situação", valor: (u) => situacaoDe(u) },
];

/* Exportação: todos os campos da ficha, na ordem das seções. Documento e financeiro só saem
   quando quem exporta marca — planilha circula por e-mail e WhatsApp, e a minimização (LGPD)
   pede que o arquivo comum não leve CPF nem conta bancária. */
const CAMPOS_SENSIVEIS = new Set(["cpf", "rg", "cnpj", "razao_social", "banco", "agencia", "conta", "tipo_conta", "chave_pix"]);

/** Valor como aparece na planilha: Sim/Não, data dd/mm/aaaa, equipe e permissão pelo nome. */
function valorPlanilha(u, campo, getNomeEquipe) {
  const v = u?.[campo];
  if (v === null || v === undefined || v === "") return "";
  const tipo = CAMPO[campo]?.type;
  if (tipo === "boolean") return sim(v) ? "Sim" : "Não";
  if (tipo === "date") return dataBR(v);
  if (campo === "team") return getNomeEquipe(v) || v;
  if (campo === "permissao") return labelPermissao(v);
  return v;
}

export function linhasExportacao(alvo, { sensiveis = false, getNomeEquipe = (v) => v } = {}) {
  const campos = CAMPOS_FORM.filter((c) => sensiveis || !CAMPOS_SENSIVEIS.has(c));
  const cab = ["ID", "Situação", "Cadastro %", "Pendências", ...campos.map((c) => CAMPO[c]?.label || c)];
  const linhas = alvo.map((u) => [u.id_usuarios || `(sem ID) ${u.id}`, SITUACAO[situacaoDe(u)].rotulo,
    percentualCadastro(u), camposFaltantes(u).map((f) => f.label).join(", "),
    ...campos.map((c) => valorPlanilha(u, c, getNomeEquipe))]);
  const larguras = cab.map((_, i) => (i === 0 ? 12 : ["endereco", "observacoes", "contato_emergencia", "link_documentos"].includes(campos[i - 4]) ? 40 : 20));
  return { linhas: [cab, ...linhas], larguras };
}

function RHUsuarios() {
  const toast = useToast();
  const { equipesOpcoes, getNomeEquipe } = useEquipes();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [aba, setAba] = useState("ativo");
  const [alerta, setAlerta] = useState("");
  const [busca, setBusca] = useState("");
  const [filtroEquipe, setFiltroEquipe] = useState("");
  const [filtroPermissao, setFiltroPermissao] = useState("");
  const [ordem, setOrdem] = useState({ coluna: "nome", direcao: 1 });
  const [condicoes, setCondicoes] = useState([]);
  const [avancadoAberto, setAvancadoAberto] = useState(false);
  const [selecionados, setSelecionados] = useState(() => new Set());
  const [calendarioAberto, setCalendarioAberto] = useState(false);
  const [calendarioData, setCalendarioData] = useState(() => new Date());
  const [aberto, setAberto] = useState(null);           // ref do usuário com a ficha aberta
  const [secaoInicial, setSecaoInicial] = useState(null);
  const [desligando, setDesligando] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { ok, data } = await apiFetch("/corretor/retornar-lista?per_page=1000");
      if (!ok || data?.error) { toast(data?.error || "Erro ao carregar usuários.", "error"); return; }
      setUsuarios(data.lista || []);
    } catch {
      toast("Erro de comunicação com a API.", "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);
  useEffect(() => { carregar(); }, [carregar]);

  const atualizarLocal = useCallback((ref, mudancas) =>
    setUsuarios((prev) => prev.map((u) => (refDe(u) === ref ? { ...u, ...mudancas } : u))), []);

  const daAba = useCallback((u) => aba === "todos" || situacaoDe(u) === aba, [aba]);
  const contagemAba = useMemo(() => Object.fromEntries(ABAS.map((a) =>
    [a.id, a.id === "todos" ? usuarios.length : usuarios.filter((u) => situacaoDe(u) === a.id).length])), [usuarios]);
  const contagemAlerta = useMemo(() => Object.fromEntries(ALERTAS.map((al) =>
    [al.id, usuarios.filter((u) => daAba(u) && al.teste(u)).length])), [usuarios, daAba]);

  const equipesUsadas = useMemo(() => {
    const usadas = new Set(usuarios.map((u) => String(u.team || "")).filter(Boolean));
    return equipesOpcoes.filter((e) => usadas.has(e.value));
  }, [usuarios, equipesOpcoes]);
  const gerentes = useMemo(() => [...new Set(usuarios
    .filter((u) => u.permissao === "gerente" && situacaoDe(u) === "ativo").map(nomeDe))].sort(), [usuarios]);

  const lista = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const col = COLUNAS.find((c) => c.id === ordem.coluna);
    const valor = col?.id === "equipe" ? (u) => getNomeEquipe(u.team) || "" : col?.valor || (() => "");
    const teste = ALERTAS.find((a) => a.id === alerta)?.teste;
    return usuarios
      .filter(daAba)
      .filter((u) => !teste || teste(u))
      .filter((u) => !filtroEquipe || String(u.team || "") === filtroEquipe)
      .filter((u) => !filtroPermissao || String(u.permissao || "") === filtroPermissao)
      .filter((u) => condicoes.every((c) => atende(u, c)))
      .filter((u) => !termo || [u.nome, u.username, u.id_usuarios, u.email_corporativo, u.email, u.cpf, u.telefone_corporativo,
        u.telefone, getNomeEquipe(u.team), u.permissao].filter(Boolean).join(" ").toLowerCase().includes(termo))
      .sort((a, b) => { const va = valor(a), vb = valor(b); return (va > vb ? 1 : va < vb ? -1 : 0) * ordem.direcao; });
  }, [usuarios, daAba, alerta, filtroEquipe, filtroPermissao, condicoes, busca, ordem, getNomeEquipe]);

  useEffect(() => { setSelecionados(new Set()); }, [aba, alerta, filtroEquipe, filtroPermissao, condicoes, busca]);

  const ordenarPor = (coluna) => setOrdem((o) => ({ coluna, direcao: o.coluna === coluna ? -o.direcao : 1 }));
  const condicoesValidas = condicoes.filter((c) => SEM_VALOR.has(c.op) || String(c.valor ?? "").trim()).length;
  const filtrosAtivos = Boolean(busca || filtroEquipe || filtroPermissao || alerta || condicoesValidas);
  const limparFiltros = () => { setBusca(""); setFiltroEquipe(""); setFiltroPermissao(""); setAlerta(""); setCondicoes([]); };

  const todosMarcados = lista.length > 0 && lista.every((u) => selecionados.has(refDe(u)));
  const marcarTodos = () => setSelecionados(todosMarcados ? new Set() : new Set(lista.map(refDe)));
  const marcar = (ref) => setSelecionados((prev) => { const n = new Set(prev); if (n.has(ref)) n.delete(ref); else n.add(ref); return n; });

  const exportarRef = useRef(null);
  const [exportarSensiveis, setExportarSensiveis] = useState(false);
  const selecionadas = lista.filter((u) => selecionados.has(refDe(u)));

  /** Baixa a planilha de `alvo` (lista da tela, todas ou selecionadas). */
  const exportar = (alvo, formato, rotulo) => {
    if (!alvo.length) { toast("Nenhuma pessoa para exportar.", "error"); return; }
    const { linhas, larguras } = linhasExportacao(alvo, { sensiveis: exportarSensiveis, getNomeEquipe });
    const nome = `rh-pessoas-${rotulo}-${dateKey(new Date())}`;
    if (formato === "xlsx") baixarArquivo(gerarXlsx(linhas, { aba: "Pessoas", larguras }), `${nome}.xlsx`, TIPO_XLSX);
    else baixarArquivo(gerarCsv(linhas), `${nome}.csv`, "text/csv;charset=utf-8");
    if (exportarRef.current) exportarRef.current.open = false;
    toast(`${alvo.length} pessoa(s) exportada(s).`, "success");
  };

  const mudarAcesso = useCallback(async (u, ativar) => {
    const nome = nomeDe(u);
    const sit = situacaoDe(u);
    if (ativar && !window.confirm(sit === "desligado"
      ? `Reativar ${nome}? O acesso volta e o desligamento deixa de valer.`
      : `Ativar o acesso de ${nome}?`)) return false;
    const { ok, data } = await apiFetch("/corretor/alterar-ativo", {
      method: "POST", body: JSON.stringify({ id_corretor: refDe(u), new_ativo: ativar }),
    });
    if (!ok || data?.error) { toast(data?.error || "Erro ao alterar o acesso.", "error"); return false; }
    atualizarLocal(refDe(u), ativar
      ? { ativo: true, desligado: false, status: data.status || "Ativo" }
      : { ativo: false, desligado: true, status: data.status || "Inativo" });
    if (ativar) toast(sit === "desligado" ? `${nome} reativado.` : `Acesso de ${nome} ativado.`, "success");
    return true;
  }, [atualizarLocal, toast]);

  const abrir = (u, secao = null) => { setSecaoInicial(secao); setAberto(refDe(u)); };
  const aberta = aberto ? usuarios.find((u) => refDe(u) === aberto) : null;

  return (
    <div className="rhx">
      <div className="rhx-shell">
        <header className="rhx-topo">
          <div>
            <span className="rhx-eyebrow"><i />Administração · RH</span>
            <h1>Pessoas</h1>
            <p>{loading ? "Carregando…" : `${contagemAba.ativo} ativas · ${contagemAba.pendente} pendentes · ${contagemAba.desligado} desligadas`}</p>
          </div>
          <div className="rhx-topo-acoes">
            <button type="button" className="rhx-sec" onClick={() => setCalendarioAberto((v) => !v)} aria-expanded={calendarioAberto}>
              {calendarioAberto ? "Ocultar calendário" : "Calendário"}
            </button>
            <details className="rhx-exportar" ref={exportarRef}>
              <summary className="rhx-sec">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M12 4v11M7 10l5 5 5-5M5 19h14" /></svg>
                Exportar <span aria-hidden="true">▾</span>
              </summary>
              <div className="rhx-exportar-menu">
                {[
                  { id: "lista", titulo: "Lista atual", desc: "aba e filtros aplicados", alvo: lista, rotulo: aba },
                  { id: "todas", titulo: "Todas as pessoas", desc: "ativas, pendentes e desligadas", alvo: usuarios, rotulo: "todas" },
                ].map((g) => (
                  <div className="rhx-exportar-grupo" key={g.id}>
                    <div className="rhx-exportar-titulo"><strong>{g.titulo}</strong><small>{g.alvo.length} pessoa(s) · {g.desc}</small></div>
                    <div className="rhx-exportar-botoes">
                      <button type="button" className="rhx-cta" disabled={!g.alvo.length || loading}
                        onClick={() => exportar(g.alvo, "xlsx", g.rotulo)}>Excel (.xlsx)</button>
                      <button type="button" className="rhx-sec" disabled={!g.alvo.length || loading}
                        onClick={() => exportar(g.alvo, "csv", g.rotulo)}>CSV</button>
                    </div>
                  </div>
                ))}
                <label className="rhx-exportar-sensiveis">
                  <input type="checkbox" checked={exportarSensiveis} onChange={(e) => setExportarSensiveis(e.target.checked)} />
                  <span>Incluir CPF, RG, CNPJ e dados bancários
                    <small>Só marque se o arquivo vai para quem precisa desses dados.</small></span>
                </label>
              </div>
            </details>
            <button type="button" className="rhx-sec" onClick={carregar} disabled={loading}>Atualizar</button>
          </div>
        </header>

        {calendarioAberto && <CalendarioRH usuarios={usuarios} data={calendarioData} setData={setCalendarioData} getNomeEquipe={getNomeEquipe} />}

        <section className="rhx-painel">
          <nav className="rhx-tabs" role="tablist" aria-label="Situação">
            {ABAS.map((a) => (
              <button key={a.id} type="button" role="tab" aria-selected={aba === a.id}
                className={aba === a.id ? "is-ativa" : ""} onClick={() => setAba(a.id)}>
                {a.rotulo}<span>{loading ? "…" : contagemAba[a.id]}</span>
              </button>
            ))}
          </nav>

          <div className="rhx-filtros">
            <div className="rhx-busca">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
              <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por nome, e-mail, telefone, ID ou CPF" aria-label="Buscar" />
            </div>
            <select value={filtroEquipe} onChange={(e) => setFiltroEquipe(e.target.value)} aria-label="Equipe">
              <option value="">Todas as equipes</option>
              {equipesUsadas.map((eq) => <option key={eq.value} value={eq.value}>{eq.label}</option>)}
            </select>
            <select value={filtroPermissao} onChange={(e) => setFiltroPermissao(e.target.value)} aria-label="Permissão">
              <option value="">Todas as permissões</option>
              {PERMISSOES.map((p) => <option key={p.value} value={p.value}>{PERMISSOES_PLURAL[p.value] || p.label}</option>)}
            </select>
            <button type="button" className={`rhx-sec ${avancadoAberto || condicoesValidas ? "is-on" : ""}`} aria-expanded={avancadoAberto}
              aria-controls="rhx-avancado" onClick={() => setAvancadoAberto((v) => !v)}>
              Filtros avançados{condicoesValidas ? <b className="rhx-badge">{condicoesValidas}</b> : null}
              <span aria-hidden="true">{avancadoAberto ? "▴" : "▾"}</span>
            </button>
            {filtrosAtivos && <button type="button" className="rhx-link" onClick={limparFiltros}>Limpar filtros</button>}
          </div>

          {avancadoAberto && (
            <FiltrosAvancados condicoes={condicoes} setCondicoes={setCondicoes} equipesOpcoes={equipesOpcoes} />
          )}

          <div className="rhx-alertas" aria-label="Alertas">
            {ALERTAS.map((al) => (
              <button key={al.id} type="button" aria-pressed={alerta === al.id} disabled={!contagemAlerta[al.id] && alerta !== al.id}
                className={`rhx-chip ${alerta === al.id ? "is-on" : ""} ${al.id === "inconsistente" && contagemAlerta[al.id] ? "is-alerta" : ""}`}
                onClick={() => setAlerta(alerta === al.id ? "" : al.id)}>
                {al.rotulo} <b>{contagemAlerta[al.id]}</b>
              </button>
            ))}
          </div>

          {selecionados.size > 0 && (
            <div className="rhx-bulk" role="status">
              <strong>{selecionados.size} selecionada(s)</strong>
              <button type="button" className="rhx-sec" onClick={() => exportar(selecionadas, "xlsx", "selecionadas")}>Exportar selecionadas (.xlsx)</button>
              <button type="button" className="rhx-sec" onClick={() => exportar(selecionadas, "csv", "selecionadas")}>CSV</button>
              <button type="button" className="rhx-link" onClick={() => setSelecionados(new Set())}>Limpar seleção</button>
            </div>
          )}

          <div className="rhx-tabela-wrap">
            <table className="rhx-tabela">
              <thead>
                <tr>
                  <th className="rhx-col-check"><input type="checkbox" checked={todosMarcados} onChange={marcarTodos} aria-label="Selecionar todas" /></th>
                  {COLUNAS.map((c) => (
                    <th key={c.id} aria-sort={ordem.coluna === c.id ? (ordem.direcao > 0 ? "ascending" : "descending") : "none"}>
                      <button type="button" onClick={() => ordenarPor(c.id)}>{c.rotulo}{ordem.coluna === c.id ? (ordem.direcao > 0 ? " ↑" : " ↓") : ""}</button>
                    </th>
                  ))}
                  <th className="rhx-col-menu"><button type="button" className="rhx-link" onClick={() => exportar(lista, "csv", aba)} disabled={!lista.length} title="Exportar o que está na tela">CSV</button></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={COLUNAS.length + 2} className="rhx-vazio">Carregando…</td></tr>
                ) : !lista.length ? (
                  <tr><td colSpan={COLUNAS.length + 2} className="rhx-vazio">Ninguém aqui{filtrosAtivos ? " com esses filtros" : ""}.</td></tr>
                ) : lista.map((u) => {
                  const ref = refDe(u);
                  const sit = situacaoDe(u);
                  const pct = percentualCadastro(u);
                  const faltam = camposFaltantes(u).length;
                  const dc = diasCreci(u);
                  const revisar = inconsistencias(u).filter((i) => i.id !== "sem_id");
                  return (
                    <tr key={ref} className={selecionados.has(ref) ? "is-sel" : ""} onClick={() => abrir(u)} tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter") abrir(u); }}>
                      <td className="rhx-col-check" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selecionados.has(ref)} onChange={() => marcar(ref)} aria-label={`Selecionar ${nomeDe(u)}`} />
                      </td>
                      <td>
                        <div className="rhx-pessoa">
                          <span className="rhx-avatar" style={{ background: corAvatar(u) }}>{iniciais(u)}</span>
                          <div>
                            <strong>{nomeDe(u)}</strong>
                            <small>{u.id_usuarios || "sem ID"} · {u.email_corporativo || u.email || u.username}</small>
                          </div>
                        </div>
                      </td>
                      <td>{getNomeEquipe(u.team) || <span className="rhx-mudo">Sem equipe</span>}</td>
                      <td>{labelPermissao(u.permissao)}</td>
                      <td>{u.telefone_corporativo || u.telefone || <span className="rhx-mudo">—</span>}</td>
                      <td>
                        <div className="rhx-barra" title={faltam ? `Faltam ${faltam} campo(s)` : "Completo"}>
                          <i style={{ width: `${pct}%` }} className={pct === 100 ? "ok" : pct < 60 ? "baixo" : ""} />
                        </div>
                        <small>{pct === 100 ? "Completo" : `${pct}% · faltam ${faltam}`}</small>
                      </td>
                      <td>
                        {dc == null ? <span className="rhx-mudo">—</span> : (
                          <span className={`rhx-creci ${dc < 0 ? "vencido" : dc <= 30 ? "vencendo" : ""}`}>
                            {dc < 0 ? `Vencido há ${-dc}d` : dc <= 30 ? `Vence em ${dc}d` : dataBR(u.validade_creci)}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`rhx-sit ${SITUACAO[sit].classe}`}>{SITUACAO[sit].rotulo}</span>
                        {revisar.length > 0 && <span className="rhx-revisar" title={revisar.map((i) => i.texto).join(" ")}>Revisar</span>}
                      </td>
                      <td className="rhx-col-menu" onClick={(e) => e.stopPropagation()}>
                        <MenuLinha
                          usuario={u}
                          onEditar={() => abrir(u)}
                          onSenha={() => abrir(u, "acesso")}
                          onAtivar={() => mudarAcesso(u, true)}
                          onDesligar={() => setDesligando(u)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <footer className="rhx-rodape-tabela">{loading ? "" : `${lista.length} de ${contagemAba[aba]} em ${ABAS.find((a) => a.id === aba).rotulo.toLowerCase()}`}</footer>
        </section>
      </div>

      {aberta && (
        <FichaPessoa
          key={refDe(aberta)}
          usuario={aberta}
          secaoInicial={secaoInicial}
          equipesOpcoes={equipesOpcoes}
          getNomeEquipe={getNomeEquipe}
          gerentes={gerentes}
          toast={toast}
          onFechar={() => setAberto(null)}
          onSalvo={(novo) => atualizarLocal(refDe(aberta), novo)}
          onAtivar={() => mudarAcesso(aberta, true)}
          onDesligar={() => setDesligando(aberta)}
        />
      )}

      {desligando && (
        <DialogoDesligamento
          usuario={desligando}
          toast={toast}
          onFechar={() => setDesligando(null)}
          onDesligado={(m) => { atualizarLocal(refDe(desligando), m); setDesligando(null); }}
        />
      )}
    </div>
  );
}

function FiltrosAvancados({ condicoes, setCondicoes, equipesOpcoes }) {
  const novaChave = () => `c${Date.now()}${Math.random().toString(36).slice(2, 6)}`;
  const adicionar = (campo = "id_imoview", op = null) => {
    const tipo = tipoFiltro(campo);
    setCondicoes((prev) => [...prev, { chave: novaChave(), campo, op: op || OPERADORES[tipo][0][0], valor: "" }]);
  };
  const alterar = (chave, mudanca) => setCondicoes((prev) => prev.map((c) => {
    if (c.chave !== chave) return c;
    const prox = { ...c, ...mudanca };
    if (mudanca.campo) {                       // trocou o campo: condição e valor voltam ao padrão do tipo
      const ops = OPERADORES[tipoFiltro(mudanca.campo)];
      if (!ops.some(([v]) => v === prox.op)) prox.op = ops[0][0];
      prox.valor = "";
    }
    return prox;
  }));
  const remover = (chave) => setCondicoes((prev) => prev.filter((c) => c.chave !== chave));
  const opcoesDe = (campo) => {
    const f = CAMPO_FILTRO[campo] || {};
    if (f.type === "permissao") return PERMISSOES.map((p) => [p.value, p.label]);
    if (f.type === "equipe") return equipesOpcoes.map((e) => [e.value, e.label]);
    return (f.options || []).map((o) => [o, o]);
  };

  return (
    <div className="rhx-avancado" id="rhx-avancado">
      <div className="rhx-avancado-atalhos">
        <span>Atalhos</span>
        {ATALHOS.map((a) => {
          const ja = condicoes.some((c) => c.campo === a.campo && c.op === a.op);
          return (
            <button key={a.rotulo} type="button" className={`rhx-chip ${ja ? "is-on" : ""}`} aria-pressed={ja}
              onClick={() => (ja
                ? setCondicoes((prev) => prev.filter((c) => !(c.campo === a.campo && c.op === a.op)))
                : adicionar(a.campo, a.op))}>
              {a.rotulo}
            </button>
          );
        })}
      </div>

      {condicoes.length > 0 && <p className="rhx-avancado-dica">Mostrando quem atende <b>todas</b> as condições:</p>}
      {condicoes.map((c, i) => {
        const tipo = tipoFiltro(c.campo);
        const opcoes = opcoesDe(c.campo);
        return (
          <div key={c.chave} className="rhx-condicao">
            <span className="rhx-condicao-e">{i === 0 ? "Onde" : "e"}</span>
            <select aria-label="Campo" value={c.campo} onChange={(e) => alterar(c.chave, { campo: e.target.value })}>
              {SECOES.map((sec) => (
                <optgroup key={sec.id} label={sec.rotulo}>
                  {(sec.id === "acesso" ? ["id_usuarios", ...sec.campos] : sec.campos).map((n) => (
                    <option key={n} value={n}>{CAMPO_FILTRO[n]?.label || n}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            <select aria-label="Condição" value={c.op} onChange={(e) => alterar(c.chave, { op: e.target.value })}>
              {OPERADORES[tipo].map(([v, r]) => <option key={v} value={v}>{r}</option>)}
            </select>
            {!SEM_VALOR.has(c.op) && (tipo === "select" ? (
              <select aria-label="Valor" value={c.valor} onChange={(e) => alterar(c.chave, { valor: e.target.value })}>
                <option value="">Escolha…</option>
                {opcoes.map(([v, r]) => <option key={v} value={v}>{r}</option>)}
              </select>
            ) : (
              <input aria-label="Valor" type={tipo === "date" ? "date" : "text"} value={c.valor}
                placeholder={tipo === "date" ? "" : "Digite…"} onChange={(e) => alterar(c.chave, { valor: e.target.value })} />
            ))}
            <button type="button" className="rhx-condicao-x" aria-label="Remover condição" onClick={() => remover(c.chave)}>×</button>
          </div>
        );
      })}

      <div className="rhx-avancado-rodape">
        <button type="button" className="rhx-link" onClick={() => adicionar()}>+ Adicionar condição</button>
        {condicoes.length > 0 && <button type="button" className="rhx-link perigo" onClick={() => setCondicoes([])}>Remover todas</button>}
      </div>
    </div>
  );
}

function MenuLinha({ usuario, onEditar, onSenha, onAtivar, onDesligar }) {
  const [aberto, setAberto] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!aberto) return undefined;
    const fora = (e) => { if (ref.current && !ref.current.contains(e.target)) setAberto(false); };
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, [aberto]);
  const sit = situacaoDe(usuario);
  const email = usuario.email_corporativo || usuario.email;
  const acao = (fn) => () => { setAberto(false); fn(); };
  return (
    <div className="rhx-menu" ref={ref}>
      <button type="button" className="rhx-menu-btn" aria-haspopup="menu" aria-expanded={aberto}
        aria-label={`Ações de ${nomeDe(usuario)}`} onClick={() => setAberto((v) => !v)}>⋯</button>
      {aberto && (
        <div className="rhx-menu-lista" role="menu">
          <button type="button" role="menuitem" onClick={acao(onEditar)}>Abrir ficha</button>
          <button type="button" role="menuitem" onClick={acao(onSenha)}>Definir senha temporária</button>
          {email && <button type="button" role="menuitem" onClick={acao(() => navigator.clipboard?.writeText(email))}>Copiar e-mail</button>}
          <hr />
          {sit === "ativo"
            ? <button type="button" role="menuitem" className="perigo" onClick={acao(onDesligar)}>Desligar…</button>
            : <button type="button" role="menuitem" onClick={acao(onAtivar)}>{sit === "desligado" ? "Reativar" : "Ativar acesso"}</button>}
        </div>
      )}
    </div>
  );
}

function FichaPessoa({ usuario, secaoInicial, equipesOpcoes, getNomeEquipe, gerentes, toast, onFechar, onSalvo, onAtivar, onDesligar }) {
  const [form, setForm] = useState(() => formDe(usuario));
  const [senha, setSenha] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [secaoVisivel, setSecaoVisivel] = useState(secaoInicial || "acesso");
  const corpoRef = useRef(null);
  const original = useMemo(() => formDe(usuario), [usuario]);
  const sit = situacaoDe(usuario);
  const faltantes = camposFaltantes({ ...usuario, ...form });
  const pct = percentualCadastro({ ...usuario, ...form });
  const avisos = inconsistencias(usuario);

  const mudados = CAMPOS_FORM.filter((c) => form[c] !== original[c]);
  const sujo = mudados.length > 0 || Boolean(senha);

  const fechar = useCallback(() => {
    if (sujo && !window.confirm("Descartar as alterações não salvas?")) return;
    onFechar();
  }, [sujo, onFechar]);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") fechar(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [fechar]);

  const irPara = useCallback((id) => {
    const alvo = corpoRef.current?.querySelector(`#rhx-sec-${id}`);
    if (alvo) corpoRef.current.scrollTo({ top: alvo.offsetTop - 12, behavior: "smooth" });
    setSecaoVisivel(id);
  }, []);
  useEffect(() => { if (secaoInicial) irPara(secaoInicial); }, [secaoInicial, irPara]);

  const aoRolar = () => {
    const corpo = corpoRef.current;
    if (!corpo) return;
    const atual = [...corpo.querySelectorAll("section[data-secao]")]
      .filter((s) => s.offsetTop - corpo.scrollTop <= 80).pop();
    if (atual) setSecaoVisivel(atual.dataset.secao);
  };

  const enviar = async (payload) => {
    const { ok, data } = await apiFetch("/corretor/editar-usuario", { method: "POST", body: JSON.stringify(payload) });
    if (!ok || data?.error) throw new Error(data?.error || "Erro ao salvar.");
    return data.usuario || {};
  };

  const salvar = async () => {
    if (!sujo) return;
    setSalvando(true);
    try {
      const payload = { id_corretor: refDe(usuario) };
      mudados.forEach((c) => { payload[c] = form[c]; });
      if (senha) payload.nova_senha = senha;
      // "Desligado? = Sim" pelo formulário também tira o acesso e derruba as sessões, como
      // o botão Desligar; "Não" num desligado reativa o acesso.
      const virouDesligado = mudados.includes("desligado") && form.desligado === "true" && sit !== "desligado";
      const virouAtivo = mudados.includes("desligado") && form.desligado === "false" && sit === "desligado";
      if (virouDesligado) {
        const r = await apiFetch("/corretor/alterar-ativo", { method: "POST", body: JSON.stringify({ id_corretor: refDe(usuario), new_ativo: false }) });
        if (!r.ok || r.data?.error) throw new Error(r.data?.error || "Erro ao tirar o acesso.");
      }
      let novo = await enviar(payload);
      if (virouAtivo) {
        const r = await apiFetch("/corretor/alterar-ativo", { method: "POST", body: JSON.stringify({ id_corretor: refDe(usuario), new_ativo: true }) });
        if (r.ok && !r.data?.error) novo = { ...novo, ativo: true, desligado: false, status: r.data.status || "Ativo" };
      }
      onSalvo(novo);
      setForm(formDe({ ...usuario, ...novo }));
      setSenha("");
      toast(senha ? "Salvo. A senha temporária vale no próximo acesso." : "Alterações salvas.", "success");
    } catch (e) {
      toast(e.message || "Erro de comunicação com a API.", "error");
    } finally {
      setSalvando(false);
    }
  };

  const corrigir = async (id) => {
    try {
      if (id === "data_sem_desligado") {
        if (!window.confirm("Apagar a data de desligamento? A pessoa continua com acesso.")) return;
        const novo = await enviar({ id_corretor: refDe(usuario), data_desligamento: "" });
        onSalvo({ ...novo, data_desligamento: null });
        setForm((f) => ({ ...f, data_desligamento: "" }));
      } else if (id === "status_desligado") {
        const novo = await enviar({ id_corretor: refDe(usuario), status: "Ativo" });
        onSalvo(novo);
        setForm((f) => ({ ...f, status: "Ativo" }));
      }
      toast("Corrigido.", "success");
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const campo = (nome) => {
    const f = CAMPO[nome] || { name: nome, label: nome };
    const obrigatorio = RH_REQUIRED_FIELDS.includes(nome);
    const valor = form[nome] ?? "";
    const vazio = obrigatorio && !String(valor).trim();
    const set = (v) => setForm((p) => ({ ...p, [nome]: v }));
    const cheio = f.type === "textarea" || f.type === "url";
    const id = `rhx-f-${nome}`;
    let entrada;
    if (f.type === "textarea") entrada = <textarea id={id} value={valor} onChange={(e) => set(e.target.value)} rows={3} />;
    else if (f.type === "boolean") {
      entrada = (
        <div className="rhx-simnao" role="radiogroup" aria-labelledby={`${id}-r`}>
          {[["true", "Sim"], ["false", "Não"]].map(([v, r]) => (
            <button key={v} type="button" role="radio" aria-checked={valor === v} className={valor === v ? "is-on" : ""}
              onClick={() => set(valor === v ? "" : v)}>{r}</button>
          ))}
        </div>
      );
    } else if (f.type === "select") {
      entrada = (
        <select id={id} value={valor} onChange={(e) => set(e.target.value)}>
          <option value="">Selecione…</option>
          {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    } else if (f.type === "permissao") {
      entrada = (
        <select id={id} value={valor} onChange={(e) => set(e.target.value)}>
          <option value="">Selecione…</option>
          {PERMISSOES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      );
    } else if (f.type === "equipe") {
      entrada = (
        <select id={id} value={valor} onChange={(e) => set(e.target.value)}>
          <option value="">Sem equipe</option>
          {equipesOpcoes.map((eq) => <option key={eq.value} value={eq.value}>{eq.label}</option>)}
          {valor && !equipesOpcoes.some((eq) => eq.value === valor) && <option value={valor}>{valor} (não cadastrada)</option>}
        </select>
      );
    } else if (nome === "gerente_responsavel") {
      entrada = (
        <>
          <input id={id} list="rhx-gerentes" value={valor} onChange={(e) => set(e.target.value)} placeholder="Escolha ou digite" />
          <datalist id="rhx-gerentes">{gerentes.map((g) => <option key={g} value={g} />)}</datalist>
        </>
      );
    } else {
      entrada = <input id={id} type={f.type || "text"} value={valor} placeholder={f.placeholder} onChange={(e) => set(e.target.value)} />;
    }
    return (
      <div key={nome} className={`rhx-campo ${cheio ? "cheio" : ""} ${vazio ? "falta" : ""} ${valor !== original[nome] ? "mudou" : ""}`}>
        <label htmlFor={id} id={`${id}-r`}>{f.label}{obrigatorio ? <em title="Obrigatório para o RH"> *</em> : null}</label>
        {entrada}
        {f.type === "url" && String(valor).trim() && <a href={String(valor).trim()} target="_blank" rel="noopener noreferrer" className="rhx-link">Abrir ↗</a>}
        {nome === "desligado" && <small>Marcar Sim tira o acesso na hora; Não reativa.</small>}
      </div>
    );
  };

  const faltamPorSecao = Object.fromEntries(SECOES.map((s) => [s.id, faltantes.filter((f) => SECAO_DO_CAMPO[f.name] === s.id).length]));

  return (
    <div className="rhx-fundo" role="presentation" onClick={fechar}>
      <aside className="rhx-ficha" role="dialog" aria-modal="true" aria-label={`Ficha de ${nomeDe(usuario)}`} onClick={(e) => e.stopPropagation()}>
        <header className="rhx-ficha-topo">
          <span className="rhx-avatar grande" style={{ background: corAvatar(usuario) }}>{iniciais(usuario)}</span>
          <div className="rhx-ficha-id">
            <h2>{nomeDe(usuario)}</h2>
            <p>
              <span className={`rhx-sit ${SITUACAO[sit].classe}`}>{SITUACAO[sit].rotulo}</span>
              {usuario.id_usuarios || "sem ID"} · {labelPermissao(usuario.permissao)} · {getNomeEquipe(usuario.team) || "Sem equipe"}
            </p>
          </div>
          <div className="rhx-ficha-acoes">
            {sit === "ativo" && <button type="button" className="rhx-perigo" onClick={onDesligar}>Desligar…</button>}
            {sit !== "ativo" && <button type="button" className="rhx-cta" onClick={onAtivar}>{sit === "desligado" ? "Reativar" : "Ativar acesso"}</button>}
            <button type="button" className="rhx-fechar" onClick={fechar} aria-label="Fechar">×</button>
          </div>
        </header>

        <div className="rhx-ficha-layout">
          <nav className="rhx-indice" aria-label="Seções">
            <div className="rhx-indice-cadastro">
              <span>Cadastro {pct}%</span>
              <div className="rhx-barra larga"><i style={{ width: `${pct}%` }} className={pct === 100 ? "ok" : pct < 60 ? "baixo" : ""} /></div>
            </div>
            {SECOES.map((s) => (
              <button key={s.id} type="button" className={secaoVisivel === s.id ? "is-ativa" : ""} onClick={() => irPara(s.id)}>
                {s.rotulo}{faltamPorSecao[s.id] ? <em>{faltamPorSecao[s.id]}</em> : null}
              </button>
            ))}
            <button type="button" className={secaoVisivel === "senha" ? "is-ativa" : ""} onClick={() => irPara("senha")}>Senha temporária</button>
          </nav>

          <div className="rhx-ficha-corpo" ref={corpoRef} onScroll={aoRolar}>
            {avisos.map((a) => (
              <div key={a.id} className={`rhx-aviso ${a.id === "sem_id" ? "info" : ""}`}>
                <span>{a.texto}</span>
                {a.id === "data_sem_desligado" && (
                  <span className="rhx-aviso-acoes">
                    <button type="button" className="rhx-perigo" onClick={onDesligar}>Desligar</button>
                    <button type="button" className="rhx-sec" onClick={() => corrigir(a.id)}>Apagar a data</button>
                  </span>
                )}
                {a.id === "status_desligado" && <button type="button" className="rhx-sec" onClick={() => corrigir(a.id)}>Corrigir status para Ativo</button>}
              </div>
            ))}

            {SECOES.map((s) => (
              <section key={s.id} id={`rhx-sec-${s.id}`} data-secao={s.id} className="rhx-secao">
                <h3>{s.rotulo}{faltamPorSecao[s.id] ? <em>{faltamPorSecao[s.id]} obrigatório(s) vazio(s)</em> : null}</h3>
                <div className="rhx-grade">
                  {s.id === "acesso" && (
                    <div className="rhx-campo"><label htmlFor="rhx-f-id">ID do usuário</label><input id="rhx-f-id" value={usuario.id_usuarios || `sem ID (interno ${usuario.id})`} disabled /></div>
                  )}
                  {s.campos.map(campo)}
                </div>
              </section>
            ))}

            <section id="rhx-sec-senha" data-secao="senha" className="rhx-secao">
              <h3>Senha temporária</h3>
              <div className="rhx-grade">
                <div className={`rhx-campo cheio ${senha ? "mudou" : ""}`}>
                  <label htmlFor="rhx-f-senha">Nova senha temporária</label>
                  <input id="rhx-f-senha" type="password" autoComplete="new-password" value={senha} onChange={(e) => setSenha(e.target.value)}
                    placeholder="Deixe em branco para não mudar" />
                  <small>A pessoa é obrigada a trocar no próximo acesso, e as sessões abertas caem.</small>
                </div>
              </div>
            </section>
          </div>
        </div>

        <footer className={`rhx-ficha-rodape ${sujo ? "sujo" : ""}`}>
          <span>{sujo ? `${mudados.length + (senha ? 1 : 0)} alteração(ões) não salva(s)` : "Tudo salvo"}</span>
          <button type="button" className="rhx-sec" disabled={!sujo || salvando} onClick={() => { setForm(original); setSenha(""); }}>Desfazer</button>
          <button type="button" className="rhx-cta" disabled={!sujo || salvando} onClick={salvar}>{salvando ? "Salvando…" : "Salvar alterações"}</button>
        </footer>
      </aside>
    </div>
  );
}

function DialogoDesligamento({ usuario, toast, onFechar, onDesligado }) {
  const [data, setData] = useState(() => usuario.data_desligamento || dateKey(new Date()));
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const nome = nomeDe(usuario);

  const confirmar = async () => {
    if (!data) { toast("Informe a data do desligamento.", "error"); return; }
    setEnviando(true);
    try {
      // 1) tira o acesso e derruba as sessões; 2) grava data e motivo (status vira Desligado).
      const r1 = await apiFetch("/corretor/alterar-ativo", { method: "POST", body: JSON.stringify({ id_corretor: refDe(usuario), new_ativo: false }) });
      if (!r1.ok || r1.data?.error) { toast(r1.data?.error || "Erro ao tirar o acesso.", "error"); return; }
      const registro = `Desligamento em ${dataBR(data)}${motivo.trim() ? `: ${motivo.trim()}` : ""}`;
      const observacoes = [usuario.observacoes, registro].filter((t) => String(t || "").trim()).join("\n");
      const r2 = await apiFetch("/corretor/editar-usuario", {
        method: "POST", body: JSON.stringify({ id_corretor: refDe(usuario), desligado: "true", data_desligamento: data, observacoes }),
      });
      if (!r2.ok || r2.data?.error) {
        toast(`Acesso retirado, mas a data não foi gravada: ${r2.data?.error || "erro"}.`, "error");
        onDesligado({ ativo: false, desligado: true, status: "Desligado" });
        return;
      }
      onDesligado(r2.data.usuario || { ativo: false, desligado: true, status: "Desligado", data_desligamento: data, observacoes });
      toast(`${nome} desligado. O acesso foi retirado.`, "success");
    } catch {
      toast("Erro de comunicação com a API.", "error");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="rhx-fundo rhx-fundo--centro" role="presentation" onClick={onFechar}>
      <div className="rhx-dialogo" role="alertdialog" aria-modal="true" aria-labelledby="rhx-deslig-titulo" onClick={(e) => e.stopPropagation()}>
        <h3 id="rhx-deslig-titulo">Desligar {nome}?</h3>
        <p>O acesso ao sistema é retirado na hora e as sessões abertas caem. Para desfazer, use <b>Reativar</b>.</p>
        <div className="rhx-campo"><label htmlFor="rhx-d-data">Data do desligamento *</label>
          <input id="rhx-d-data" type="date" value={data} onChange={(e) => setData(e.target.value)} />
        </div>
        <div className="rhx-campo"><label htmlFor="rhx-d-motivo">Motivo (vai para as observações)</label>
          <textarea id="rhx-d-motivo" rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Opcional" />
        </div>
        <div className="rhx-acoes">
          <button type="button" className="rhx-sec" onClick={onFechar} disabled={enviando}>Cancelar</button>
          <button type="button" className="rhx-perigo" onClick={confirmar} disabled={enviando}>{enviando ? "Desligando…" : "Desligar"}</button>
        </div>
      </div>
    </div>
  );
}

// Google Agenda: um .ics com os aniversários repetindo todo ano, importado na agenda da
// pessoa. Sem login do Google no sistema. UID fixo por pessoa, para o Google reconhecer o
// mesmo evento quando a lista for importada de novo na mesma agenda.
const GOOGLE_IMPORTAR = "https://calendar.google.com/calendar/u/0/r/settings/export";

const icsTexto = (v) => String(v || "").replace(/\\/g, "\\\\").replace(/([;,])/g, "\\$1").replace(/\r?\n/g, "\\n");

function eventoAnual(uid, inicio, titulo, descricao) {
  const [a, m, d] = inicio.split("-").map(Number);
  const fimDia = new Date(Date.UTC(a, m - 1, d + 1)).toISOString().slice(0, 10).replace(/-/g, "");
  // 29/02: o último dia de fevereiro, para não sumir nos anos que não são bissextos.
  const regra = m === 2 && d === 29 ? "RRULE:FREQ=YEARLY;BYMONTH=2;BYMONTHDAY=-1" : "RRULE:FREQ=YEARLY";
  return ["BEGIN:VEVENT", `UID:${uid}@inteligencia61imoveis.com.br`, "DTSTAMP:20260101T000000Z",
    `DTSTART;VALUE=DATE:${inicio.replace(/-/g, "")}`, `DTEND;VALUE=DATE:${fimDia}`, regra,
    `SUMMARY:${icsTexto(titulo)}`, `DESCRIPTION:${icsTexto(descricao)}`, "TRANSP:TRANSPARENT", "END:VEVENT"];
}

export function gerarIcsAniversarios(usuarios, getNomeEquipe = () => "") {
  const linhas = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Inteligencia 61//RH//PT-BR", "CALSCALE:GREGORIAN",
    "X-WR-CALNAME:Aniversários 61"];
  usuarios.filter((u) => !sim(u.desligado)).forEach((u) => {
    const chave = u.id ?? u.id_usuarios ?? u.username;
    const equipe = getNomeEquipe(u.team);
    if (/^\d{4}-\d{2}-\d{2}/.test(String(u.data_nascimento || ""))) {
      linhas.push(...eventoAnual(`nascimento-${chave}`, String(u.data_nascimento).slice(0, 10),
        `Aniversário: ${nomeDe(u)}`, equipe));
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(String(u.data_entrada_61 || ""))) {
      const entrada = String(u.data_entrada_61).slice(0, 10);
      const [a, m, d] = entrada.split("-");
      // Começa no 1º aniversário de casa: no dia da entrada a pessoa ainda tem 0 ano.
      linhas.push(...eventoAnual(`casa-${chave}`, `${Number(a) + 1}-${m}-${d}`,
        `Tempo de 61: ${nomeDe(u)} (desde ${a})`,
        [`Entrou na 61 em ${d}/${m}/${a}`, equipe].filter(Boolean).join(" · ")));
    }
  });
  linhas.push("END:VCALENDAR");
  return linhas.join("\r\n") + "\r\n";
}

function CalendarioRH({ usuarios, data, setData, getNomeEquipe }) {
  const [ajudaGoogle, setAjudaGoogle] = useState(false);
  const levarParaGoogle = () => {
    baixarArquivo(gerarIcsAniversarios(usuarios, getNomeEquipe), "aniversarios-61.ics", "text/calendar;charset=utf-8");
    window.open(GOOGLE_IMPORTAR, "_blank", "noopener,noreferrer");
    setAjudaGoogle(true);
  };
  const ano = data.getFullYear();
  const mes = data.getMonth();
  const dias = useMemo(() => {
    const eventos = {};
    const add = (k, e) => { (eventos[k] = eventos[k] || []).push(e); };
    feriadosBrasil(ano).forEach((f) => add(f.key, f));
    usuarios.forEach((u) => {
      if (!u.data_nascimento || sim(u.desligado)) return;
      const [, m, d] = String(u.data_nascimento).split("-");
      if (m && d) add(`${ano}-${m}-${d}`, { tipo: "aniversario", titulo: nomeDe(u), detalhe: getNomeEquipe(u.team) });
    });
    // Aniversário de casa: data de entrada na 61. O ano de entrada não conta (0 anos).
    usuarios.forEach((u) => {
      if (!u.data_entrada_61 || sim(u.desligado)) return;
      const [a, m, d] = String(u.data_entrada_61).split("-");
      const anos = ano - Number(a);
      if (!m || !d || !(anos > 0)) return;
      add(`${ano}-${m}-${d}`, {
        tipo: "casa", titulo: nomeDe(u),
        detalhe: [`${anos} ${anos === 1 ? "ano" : "anos"} de 61`, getNomeEquipe(u.team)].filter(Boolean).join(" · "),
      });
    });
    const primeiro = new Date(ano, mes, 1);
    const inicio = addDays(primeiro, -primeiro.getDay());
    const hoje = dateKey(new Date());
    return Array.from({ length: 42 }, (_, i) => {
      const dt = addDays(inicio, i);
      const k = dateKey(dt);
      const ev = [...(eventos[k] || [])];
      if (dt.getDay() === 1) ev.unshift({ tipo: "smart", titulo: "Smart Monday" });
      return { k, dia: dt.getDate(), doMes: dt.getMonth() === mes, hoje: k === hoje, ev };
    });
  }, [usuarios, ano, mes, getNomeEquipe]);
  const doMes = dias.filter((d) => d.doMes).flatMap((d) => d.ev.filter((e) => e.tipo !== "smart").map((e) => ({ ...e, dia: d.dia })));

  return (
    <section className="rhx-cal">
      <div>
        <div className="rhx-cal-topo">
          <h3>{MESES[mes]} {ano}</h3>
          <div>
            <button type="button" onClick={() => setData(new Date(ano, mes - 1, 1))} aria-label="Mês anterior">‹</button>
            <button type="button" onClick={() => setData(new Date())}>Hoje</button>
            <button type="button" onClick={() => setData(new Date(ano, mes + 1, 1))} aria-label="Próximo mês">›</button>
          </div>
        </div>
        <div className="rhx-cal-semana">{DIAS_SEMANA.map((d) => <span key={d}>{d}</span>)}</div>
        <div className="rhx-cal-grade">
          {dias.map((d) => (
            <div key={d.k} className={`${d.doMes ? "" : "fora"} ${d.hoje ? "hoje" : ""}`}>
              <span>{d.dia}</span>
              {d.ev.slice(0, 2).map((e, i) => <em key={i} className={e.tipo} title={e.detalhe || undefined}>{e.titulo}</em>)}
              {d.ev.length > 2 && <small>+{d.ev.length - 2}</small>}
            </div>
          ))}
        </div>
      </div>
      <aside className="rhx-cal-lista">
        <h4>Feriados e aniversários</h4>
        <p className="rhx-cal-legenda"><i className="aniversario" />Nascimento <i className="casa" />Tempo de 61</p>
        <button type="button" className="rhx-cal-google" onClick={levarParaGoogle}>Adicionar ao Google Agenda</button>
        {ajudaGoogle && (
          <ol className="rhx-cal-google-ajuda">
            <li>O arquivo <b>aniversarios-61.ics</b> foi baixado.</li>
            <li>Na aba do Google Agenda que abriu, em <b>Importar</b>, escolha o arquivo e a agenda (dica: crie antes uma agenda "Aniversários 61").</li>
            <li>Clique em <b>Importar</b>. Os aniversários se repetem todo ano. Entrou gente nova? Importe de novo na mesma agenda.</li>
          </ol>
        )}
        {!doMes.length ? <p>Nenhum evento neste mês.</p> : doMes.map((e, i) => (
          <div key={i} className={e.tipo}><strong>{String(e.dia).padStart(2, "0")}</strong><span>{e.titulo}{e.detalhe ? <small>{e.detalhe}</small> : null}</span></div>
        ))}
      </aside>
    </section>
  );
}

export default RHUsuarios;
